import { useEffect as useEffectH, useRef as useRefH, useState as useStateH } from "react";
import { Api } from "./api";
import { Banner, BylawRow, Field, Icon, Logo } from "./components";
import { STARTER_PROMPTS } from "./data";

// Homeowner experience: chat, history, bylaws library, directory.

function firstNameOf(user) {
  const name = (user?.full_name || "").trim();
  if (name) return name.split(/\s+/)[0];
  return (user?.email || "").split("@")[0];
}

/* ─── Render the model's answer into JSX ─────────────────────────────────── */
// The model emits a lightly-marked text (markdown headings, **bold**, "- "
// bullets, and inline source refs like "[Source 1]" or "[Source 1, Source 3]").
// We don't pull in a full markdown library — handle the small subset the
// system prompt actually uses.
function renderAnswer(text, onCite) {
  const blocks = text.split("\n\n");
  return blocks.map((para, pi) => {
    const trimmed = para.trim();
    // Headings: # / ## / ### at the start of a block.
    const h = trimmed.match(/^(#{1,3})\s+(.*)$/s);
    if (h) {
      const level = h[1].length; // 1, 2, or 3
      const Tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
      // Strip any trailing # closing fence the model occasionally adds.
      const inner = h[2].replace(/\s+#+\s*$/, "");
      return (
        <Tag key={pi} className="msg__head" data-level={level}>
          {renderInline(inner, onCite)}
        </Tag>
      );
    }
    // Bullet list: every non-empty line in the block starts with "- ".
    if (trimmed.startsWith("- ")) {
      const items = para.split("\n").filter((l) => l.trim().startsWith("- "));
      return (
        <ul key={pi} style={{ margin: "0 0 12px 18px", padding: 0 }}>
          {items.map((it, ii) => (
            <li key={ii} style={{ margin: "0 0 6px" }}>
              {renderInline(it.replace(/^\s*-\s*/, ""), onCite)}
            </li>
          ))}
        </ul>
      );
    }
    return <p key={pi}>{renderInline(para, onCite)}</p>;
  });
}
function renderInline(text, onCite) {
  // Match: [cite:N], [Source N] (with optional ", Source M" repeats), **bold**.
  const parts = [];
  let rest = text;
  let i = 0;
  // Match: [Source N], [Source N, Source M, ...], OR the new combined
  // shape [Source N, Section X.Y] / [Source N, Source M, Article IV §2]
  // where the trailing comma-separated tail can be ANY section reference
  // (the system prompt now asks Claude to include the bylaw section ref
  // inline next to the source label). Group 1 captures only the
  // `Source N(, Source M)*` part — section refs are intentionally
  // ignored so the pill set stays just the source labels.
  const SRC_RE = /\[Source\s+(\d+(?:\s*,\s*Source\s+\d+)*)(?:\s*,\s*[^\]]+)?\]/;
  while (rest.length) {
    const cite = rest.match(/\[cite:(\d+)\]/);
    const src = rest.match(SRC_RE);
    const bold = rest.match(/\*\*([^*]+)\*\*/);
    const next = [cite, src, bold].filter(Boolean).sort((a, b) => a.index - b.index)[0];
    if (!next) { parts.push(rest); break; }
    if (next.index > 0) parts.push(rest.slice(0, next.index));
    if (next === cite) {
      const n = +next[1];
      parts.push(
        <button key={`c${i++}`} className="msg__cite" onClick={() => onCite(n)} title="Show citation">
          {n}
        </button>
      );
    } else if (next === src) {
      // [Source 1, Source 3] → render one pill per number so the user can
      // tap each individually if we ever wire click-to-jump.
      const nums = next[1].split(",").map((s) => parseInt(s.trim().replace(/^Source\s+/i, ""), 10)).filter(Number.isFinite);
      nums.forEach((n) => {
        parts.push(
          <button key={`s${i++}`} className="msg__cite" onClick={() => onCite(n)} title={`Source ${n}`}>
            {n}
          </button>
        );
      });
    } else {
      parts.push(<strong key={`b${i++}`}>{next[1]}</strong>);
    }
    rest = rest.slice(next.index + next[0].length);
  }
  return parts;
}

/* ─── Chat screen ────────────────────────────────────────────────────────── */
// Friendly explainer rendered to family-member (non-primary) seats
// inside HomeownerChat. Kept as its own component so it stays cheap
// to render and the parent can decide conditionally WITHOUT a
// pre-hooks early return that violates the rules of hooks (the
// HomeownerChat user prop can flip from primary→secondary between
// the slim login payload and the refreshed /me payload).
function HomeownerChatSecondaryExplainer({ onRoute }) {
  return (
    <div className="chat">
      <div className="chat__stream">
        <div className="chat__inner">
          <div className="chat__welcome">
            <Logo size={56} />
            <h1 className="display" style={{ marginTop: 16, textWrap: "pretty" }}>
              Compass chat is your <em style={{ color: "var(--accent-ink)", fontStyle: "italic" }}>household primary's</em> seat.
            </h1>
            <p>
              Your household has one AI-enabled account — the primary — who can ask Compass
              bylaw questions and run Project Builder polish. The other community features
              (events, directory, services, assistance, messages) are open to everyone in
              the household.
            </p>
            <div className="row" style={{ gap: 8, marginTop: 16, justifyContent: "center" }}>
              <button className="btn btn--primary" onClick={() => onRoute && onRoute("settings")}>
                Open Household settings
              </button>
            </div>
            <p className="text-sm text-mute" style={{ marginTop: 12 }}>
              The primary can transfer the seat to your account at any time from Settings → Household.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeownerChat({ user, initialSessionId, onSessionStarted, onRoute, onOpenBylaw }) {
  const [thread, setThread] = useStateH([]); // start empty -> show welcome
  const [draft, setDraft] = useStateH("");
  const [pending, setPending] = useStateH(false);
  const [error, setError] = useStateH(null);
  const [sessionId, setSessionId] = useStateH(initialSessionId || null);
  const [loadingHistory, setLoadingHistory] = useStateH(Boolean(initialSessionId));
  const streamRef = useRefH(null);
  const taRef = useRefH(null);
  const communityName = user?.community?.name || "your community";

  // AI features are primary-only (pricing.md v10). Secondaries see
  // an explainer card instead of the composer — but we compute the
  // condition AFTER all hooks below so React's rules-of-hooks ordering
  // stays stable across renders when `user` flips from a slim login
  // payload (no is_household_primary) to the refreshed /me payload
  // (is_household_primary: false). Codex caught the original
  // pre-hooks early return as a crash bug.

  useEffectH(() => {
    if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
  }, [thread, pending]);

  // Preload an existing session when the History page passes one in.
  // We tell the parent "we have it" so it can clear its handoff state and
  // a later remount of this component doesn't re-load the same session.
  useEffectH(() => {
    if (!initialSessionId) return;
    let cancelled = false;
    setLoadingHistory(true);
    setError(null);
    Api.chatSession(initialSessionId)
      .then((data) => {
        if (cancelled) return;
        setSessionId(data.id);
        setThread((data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          citations: m.citations || [],
          sources: m.sources || [],
        })));
        if (onSessionStarted) onSessionStarted(data.id);
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Couldn't load that conversation."); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  const startNew = () => {
    setThread([]);
    setSessionId(null);
    setError(null);
    setDraft("");
  };

  const ask = async (q) => {
    const question = (q ?? draft).trim();
    if (!question || pending) return;
    setThread((t) => [...t, { role: "user", content: question }]);
    setDraft("");
    setPending(true);
    setError(null);
    try {
      const data = await Api.chatAsk({ question, session_id: sessionId || undefined });
      const wasNew = !sessionId;
      setSessionId(data.session_id);
      setThread((t) => [...t, {
        role: "assistant",
        content: data.answer,
        citations: data.citations || [],
        sources: data.sources || [],
        usage: data.usage,
      }]);
      if (wasNew && onSessionStarted) onSessionStarted(data.session_id);
    } catch (err) {
      // Surface API errors inline and roll the user's message back into the
      // draft so they can retry without re-typing.
      setThread((t) => t.slice(0, -1));
      setDraft(question);
      setError(err.message || "Couldn't send your question.");
    } finally {
      setPending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  // Secondary-seat short-circuit. All hooks above this run on every
  // render regardless of household status (so React's hook ordering
  // stays stable when the user payload refreshes mid-render).
  if (user?.is_household_primary === false) {
    return <HomeownerChatSecondaryExplainer onRoute={onRoute} />;
  }

  return (
    <div className="chat">
      <div className="chat__stream" ref={streamRef}>
        <div className="chat__inner">
          {loadingHistory ? (
            <div className="chat__welcome" style={{ color: "var(--mute)" }}>Loading conversation…</div>
          ) : thread.length === 0 ? (
            <Welcome user={user} onPick={ask} />
          ) : (
            thread.map((m, i) => (
              <Message key={i} m={m} onOpenBylaw={onOpenBylaw} />
            ))
          )}
          {pending && <Thinking />}
          {error && (
            <div role="alert" style={{
              marginTop: 12, padding: "12px 14px", borderRadius: 10,
              background: "var(--danger-soft, #fdecea)",
              color: "var(--danger, #b3261e)",
              border: "1px solid var(--danger, #b3261e)",
              fontSize: 14,
            }}>
              {error}
            </div>
          )}
        </div>
      </div>

      <div className="composer">
        <div className="composer__inner">
          <textarea
            ref={taRef}
            placeholder={`Ask about ${communityName}'s bylaws — pets, rentals, parking, fees, anything…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            disabled={loadingHistory}
          />
          <button
            className="composer__send"
            disabled={!draft.trim() || pending || loadingHistory}
            onClick={() => ask()}
            title="Send (Enter)"
          >
            <Icon name="send" size={16} />
          </button>
        </div>
        <div className="composer__meta">
          <div className="row">
            <Icon name="shield" size={12} />
            <span>Answers come from <b>{communityName}'s own bylaws</b> and cite the section.</span>
          </div>
          {thread.length > 0 && (
            <button className="btn btn--ghost btn--sm" onClick={startNew} style={{ marginLeft: 8 }}>
              <Icon name="plus" size={13} /> New chat
            </button>
          )}
          <div className="row" style={{ marginLeft: "auto" }}>
            <span className="kbd">Enter</span> to send · <span className="kbd">Shift</span> + <span className="kbd">Enter</span> for new line
          </div>
        </div>
      </div>
    </div>
  );
}

function Welcome({ user, onPick }) {
  const firstName = firstNameOf(user) || "there";
  const communityName = user?.community?.name || "your community";
  return (
    <div className="chat__welcome">
      <Logo size={56} />
      <h1 className="display" style={{ marginTop: 16, textWrap: "pretty" }}>
        Hi {firstName}. What can I help you find <em style={{ color: "var(--accent-ink)", fontStyle: "italic" }}>in the bylaws?</em>
      </h1>
      <p>I read <span className="text-mono" style={{ color: "var(--ink-2)" }}>{communityName}</span>'s
      active bylaws. Every answer I give cites the exact section.</p>

      <div className="chat__suggestions">
        {STARTER_PROMPTS.slice(0, 6).map((p, i) => (
          <button key={i} className="chat__suggestion" onClick={() => onPick(p.q)}>
            <div className="chat__suggestion-cat">{p.cat}</div>
            <div className="chat__suggestion-q">{p.q}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Message({ m, onOpenBylaw }) {
  if (m.role === "user") {
    return (
      <div className="msg msg__user">
        <div className="msg__role">You</div>
        <div className="msg__bubble">{m.content}</div>
      </div>
    );
  }
  const cites = Array.isArray(m.citations) ? m.citations : [];
  const sources = Array.isArray(m.sources) ? m.sources : [];
  // label -> source-object, used to resolve both inline [Source N]
  // pills AND the "Sources cited above" footer entries (which can now
  // look up the bylaw title and document id from the citation's
  // `source` label so the footer doesn't re-number citations 1..N
  // independently of the inline labels — matches the system prompt
  // contract that asks Claude to put the inline label in citation.source.
  const labelToSource = {};
  sources.forEach((s) => {
    if (s && typeof s.label === "number") labelToSource[s.label] = s;
  });
  const openCite = (label) => {
    const src = labelToSource[label];
    if (src && src.bylaw_document_id && onOpenBylaw) onOpenBylaw(src.bylaw_document_id);
  };
  return (
    <div className="msg msg__bot">
      <div className="msg__role">
        <Logo size={14} /> Compass
      </div>
      <div className="msg__bubble">
        {renderAnswer(m.content, openCite)}
      </div>
      {cites.length > 0 && (
        <div className="msg__sources">
          <div className="msg__sources-hd"><Icon name="quote" size={12} /> Sources cited above</div>
          {cites.map((c, i) => {
            // Resolve the citation's source label in priority order:
            //   1. explicit `c.source` — accept either number or
            //      digit-string (the backend's parser does the same;
            //      Claude occasionally serializes the integer as a
            //      string in valid JSON).
            //   2. first "Source N" mention in `c.location` (legacy
            //      pre-prompt-change citations that embedded the label
            //      in the location string)
            //   3. null → fall back to i+1 indexing for very old rows
            let label = null;
            if (typeof c?.source === "number" && Number.isFinite(c.source)) {
              label = c.source;
            } else if (typeof c?.source === "string" && /^\d+$/.test(c.source.trim())) {
              label = parseInt(c.source.trim(), 10);
            }
            if (label == null && typeof c?.location === "string") {
              const m = c.location.match(/\bSource\s+(\d+)\b/i);
              if (m) label = parseInt(m[1], 10);
            }
            const displayNum = label != null ? label : i + 1;
            const src = label != null ? labelToSource[label] : null;
            // Title: prefer the looked-up bylaw doc title (clean human
            // text) over whatever the model wrote in `location`, which
            // historically leaked the raw storage filename + numeric id.
            // Final fallback: `c.bylaw_title` from the backend's older
            // single-source enrichment path (chat/routes.py:932+).
            const title = (src && src.bylaw_title) || c.bylaw_title || "";
            const heading = (src && src.heading) || c.heading || "";
            // Subline: section/article reference. Model is now told to
            // put ONLY the section ref here (not the doc filename).
            const subline = [heading, c.location || c.page]
              .filter((s) => s && String(s).trim())
              .join(" · ");
            // Skip the quote line entirely when Claude omitted a quote
            // (or supplied an empty one) so the user doesn't see a bare
            // `""` pair. This was the most visible issue on the original
            // screenshot — the model frequently structurally cites
            // without a quotable phrase.
            const quote = (c.quote || c.text || "").trim();
            const docId = (src && src.bylaw_document_id) || c.bylaw_document_id;
            const isClickable = docId && onOpenBylaw;
            const inner = (
              <>
                <span className="msg__source-num">{displayNum}</span>
                <div>
                  {title && <div className="msg__source-doc">{title}</div>}
                  {quote && <div className="msg__source-quote">&ldquo;{quote}&rdquo;</div>}
                </div>
                {subline && <div className="msg__source-meta">{subline}</div>}
              </>
            );
            return isClickable ? (
              <button
                key={i}
                type="button"
                className="msg__source msg__source--link"
                onClick={() => onOpenBylaw(docId)}
                title="Open this bylaw document"
              >
                {inner}
              </button>
            ) : (
              <div key={i} className="msg__source">{inner}</div>
            );
          })}
        </div>
      )}
      {m.usage && (
        <div className="msg__actions">
          <span style={{ marginLeft: "auto" }} className="text-mono text-xs">
            {m.usage.input_tokens?.toLocaleString?.() || m.usage.input_tokens} in · {m.usage.output_tokens?.toLocaleString?.() || m.usage.output_tokens} out tokens
          </span>
        </div>
      )}
    </div>
  );
}

function Thinking() {
  return (
    <div className="msg msg__bot">
      <div className="msg__role"><Logo size={14} /> Compass · reading bylaws…</div>
      <div style={{ display: "flex", gap: 4, padding: "6px 0" }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "var(--mute-2)",
            animation: `blink 1.2s ${i * 0.18}s infinite`,
          }} />
        ))}
        <style>{`@keyframes blink { 0%, 80%, 100% { opacity: .25 } 40% { opacity: 1 } }`}</style>
      </div>
    </div>
  );
}

/* ─── History ────────────────────────────────────────────────────────────── */
function relativeDate(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const ms = Date.now() - then.getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day} days ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function HomeownerHistory({ onOpen }) {
  const [state, setState] = useStateH({ loading: true, error: null, sessions: [] });

  useEffectH(() => {
    let cancelled = false;
    Api.chatSessions()
      .then((sessions) => { if (!cancelled) setState({ loading: false, error: null, sessions: sessions || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load conversations.", sessions: [] }); });
    return () => { cancelled = true; };
  }, []);

  const { loading, error, sessions } = state;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 820 }}>
        <Banner icon="info">
          <b>Private to you.</b> Your community's manager can see <i>how many</i> questions
          you've asked, but never the contents. Only you see this list.
        </Banner>
        {loading && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div>
        )}
        {error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{error}</div>
        )}
        {!loading && !error && sessions.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            No conversations yet. Ask Compass a question and it'll show up here.
          </div>
        )}
        {!loading && !error && sessions.length > 0 && (
          <div className="card" style={{ padding: 0 }}>
            {sessions.map((s, i) => (
              <button
                key={s.id}
                onClick={() => onOpen?.(s)}
                style={{
                  display: "grid", gridTemplateColumns: "1fr auto auto",
                  gap: 16, alignItems: "center", width: "100%",
                  padding: "16px 20px", textAlign: "left",
                  borderTop: i === 0 ? "0" : "1px solid var(--hairline)",
                }}
              >
                <div>
                  <div className="display" style={{ fontSize: 17, fontWeight: 400, letterSpacing: "-0.01em", color: "var(--ink)" }}>{s.title || "(untitled)"}</div>
                  <div className="text-sm text-mute" style={{ marginTop: 3 }}>
                    last activity {relativeDate(s.last_message_at)}
                  </div>
                </div>
                <span className="pill pill--ghost text-mono">{(s.id || "").slice(0, 6).toUpperCase()}</span>
                <Icon name="chevron" size={16} style={{ color: "var(--mute)" }} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Bylaws library (read view) ─────────────────────────────────────────── */
function _fmtBytesH(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function _fmtDateH(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function BylawsLibrary({ openDocId, onOpenConsumed }) {
  const [query, setQuery] = useStateH("");
  const [state, setState] = useStateH({ loading: true, error: null, docs: [] });
  const [openId, setOpenId] = useStateH(null);

  useEffectH(() => {
    let cancelled = false;
    Api.bylawsList()
      .then((docs) => { if (!cancelled) setState({ loading: false, error: null, docs: docs || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load bylaws.", docs: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Deep-link from chat: open this doc on mount / when the prop changes.
  // Consume the request so a back-then-forward inside the library doesn't
  // forcibly re-open it.
  useEffectH(() => {
    if (openDocId != null) {
      setOpenId(openDocId);
      if (onOpenConsumed) onOpenConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDocId]);

  const { loading, error, docs } = state;
  const visible = !query ? docs : docs.filter((d) =>
    (d.title || "").toLowerCase().includes(query.toLowerCase()) ||
    (d.description || "").toLowerCase().includes(query.toLowerCase())
  );

  const newest = docs.length > 0
    ? docs.reduce((a, b) => (new Date(a.uploaded_at) > new Date(b.uploaded_at) ? a : b))
    : null;

  if (openId) {
    const meta = docs.find((d) => d.id === openId);
    return <BylawReader docId={openId} meta={meta} onBack={() => setOpenId(null)} />;
  }

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 880 }}>
        <Banner icon="book">
          {loading
            ? <b>Loading bylaws…</b>
            : docs.length === 0
              ? <><b>No bylaws yet.</b> Your community manager hasn't uploaded any documents — Compass can't answer questions until they do.</>
              : <><b>{docs.length} active {docs.length === 1 ? "document" : "documents"}.</b> {newest && <> Last updated {_fmtDateH(newest.uploaded_at)}.</>} Tap a document to read it, or ask Compass a question and it'll cite back to these.</>
          }
        </Banner>
        {!loading && !error && docs.length > 0 && (
          <div className="row" style={{ marginBottom: 6 }}>
            <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
              <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
              <input
                className="input"
                aria-label="Search bylaws"
                placeholder="Search bylaws — sections, terms, anything…"
                style={{ border: 0, padding: 0, background: "transparent" }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
        )}
        {error && <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{error}</div>}
        {!loading && !error && docs.length > 0 && visible.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            No matches for "{query}".
          </div>
        )}
        <div className="stack-md">
          {visible.map((d) => (
            <button
              key={d.id}
              onClick={() => setOpenId(d.id)}
              className="bylaw"
              style={{
                cursor: "pointer", textAlign: "left",
                width: "100%", border: "1px solid var(--hairline)",
                background: "var(--surface)",
              }}
              title="Open document"
            >
              <div className="bylaw__ico" />
              <div>
                <div className="bylaw__title">{d.title}</div>
                <div className="bylaw__meta">
                  <span className="pill pill--green"><span className="pill__dot" /> Active</span>
                  <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                  <span className="mono">v{d.version}</span>
                  <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                  <span className="mono">{_fmtBytesH(d.size_bytes)}</span>
                  <span style={{ margin: "0 8px", color: "var(--hairline-2)" }}>·</span>
                  Updated {_fmtDateH(d.uploaded_at)}
                </div>
                {d.description && <div className="text-sm text-mute" style={{ marginTop: 4 }}>{d.description}</div>}
              </div>
              <Icon name="chevron" size={16} style={{ color: "var(--mute)", alignSelf: "center" }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BylawReader({ docId, meta, onBack }) {
  const [state, setState] = useStateH({ loading: true, error: null, data: null });
  // In-doc search. `query` is what the user typed; `activeIdx` is which
  // match is currently scrolled into view (0-based). All matches are
  // highlighted in yellow; the active one is brighter so the eye can
  // find where it lands after a scroll.
  const [query, setQuery] = useStateH("");
  const [activeIdx, setActiveIdx] = useStateH(0);
  // navTick bumps on every step() call so the scroll-to-match effect
  // re-runs even when activeIdx doesn't change. Cases this matters:
  //   - matchCount === 1: pressing > wraps (0 + 1 + 1) % 1 = 0, so
  //     activeIdx stays at 0, React skips the re-render, the effect
  //     never re-runs, and the user sees "no arrow action".
  //   - Multi-match where the user manually scrolled the doc away
  //     from the active mark and then taps the same direction twice:
  //     same activeIdx, no re-render, no re-scroll — they want the
  //     button to bring them back to the match either way.
  const [navTick, setNavTick] = useStateH(0);
  const articleRef = useRefH(null);
  // Sticky-bar offset is now handled by `scroll-margin-top` on
  // `.bylaw-hit` in styles.css — no JS ref needed.

  useEffectH(() => {
    let cancelled = false;
    Api.bylawsContent(docId)
      .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load this bylaw.", data: null }); });
    return () => { cancelled = true; };
  }, [docId]);

  // Reset in-doc search state when the document changes. The reader
  // stays mounted across docId changes (e.g. when the chat citation
  // deep-link sets openDocId on BylawsLibrary, which then routes a
  // new docId here); without this the previous doc's query + active
  // match index would carry over and auto-scroll into the new doc
  // at the first incidental match (codex P2 round 2 on PR #209).
  useEffectH(() => {
    setQuery("");
    setActiveIdx(0);
  }, [docId]);

  // Count of matches in the current doc, derived live from the query.
  // Re-runs only on query / content change; the regex result array is
  // cheap to build vs the full re-render.
  //
  // Gate on the RAW trimmed query length, not the escaped version —
  // _escapeReSrc prepends a backslash for each regex metachar, so a
  // single-char query like '.' or '$' would otherwise become a
  // 2-char escaped string, satisfy the >= 2 gate here, and surface a
  // match counter for an input that _renderHighlighted (which gates
  // on q.length) correctly refuses to highlight (codex P2 on PR #209).
  const content = state.data?.content || "";
  const trimmedQ = query.trim();
  const escapedQ = _escapeReSrc(trimmedQ);
  const matchCount = (trimmedQ.length >= 2 && content)
    ? (content.match(new RegExp(escapedQ, "gi")) || []).length
    : 0;

  // Reset the active match whenever the query changes; first hit
  // scrolls into view via the effect below.
  useEffectH(() => { setActiveIdx(0); }, [query]);

  // Auto-scroll the active match into view. Uses native
  // `scrollIntoView` + a CSS `scroll-margin-top` on `.bylaw-hit` so
  // the browser handles the scroll on every device, including iOS
  // Safari where the previous manual `scrollTo({behavior:"smooth"})`
  // path silently no-op'd (different scroll ancestor on the
  // collapsed-mobile layout, and iOS Safari < 16 ignores smooth
  // scroll on non-document scrollers — the loop didn't catch
  // either failure mode, so the user saw the counter tick but
  // nothing moved).
  //
  // The sticky search bar is accounted for by `scroll-margin-top`
  // in styles.css, which is what the CSSWG designed this property
  // for — no manual viewport math needed.
  useEffectH(() => {
    if (!articleRef.current) return;
    if (matchCount === 0) return;
    const marks = articleRef.current.querySelectorAll("mark.bylaw-hit");
    const target = marks[Math.min(activeIdx, marks.length - 1)];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeIdx, matchCount, query, navTick]);

  const step = (delta) => {
    if (matchCount === 0) return;
    setActiveIdx((i) => (i + delta + matchCount) % matchCount);
    // Always bump navTick so the scroll effect re-runs even when the
    // index wraps to the same value (matchCount === 1 case) or the
    // user is re-tapping the same direction past the natural end.
    setNavTick((n) => n + 1);
  };
  const onSearchKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      step(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  const title = state.data?.title || meta?.title || "Bylaw";

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 760 }}>
        <button
          className="btn btn--ghost btn--sm"
          onClick={onBack}
          style={{ alignSelf: "flex-start" }}
        >
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Back to library
        </button>

        <div className="card card--pad-lg">
          <h2 className="display" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25 }}>{title}</h2>
          {state.data && (
            <div className="text-sm text-mute" style={{ marginTop: 6 }}>
              v{state.data.version} · {_fmtBytesH(state.data.size_bytes)} · uploaded {_fmtDateH(state.data.uploaded_at)}
            </div>
          )}
        </div>

        {state.loading && <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div>}
        {state.error && <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error}</div>}

        {state.data && !state.data.has_content && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)" }}>
            <b style={{ color: "var(--ink)" }}>No preview available for this document.</b><br />
            It was uploaded before in-app preview was enabled, or it's a scanned PDF without an extractable text layer. Ask your community manager to re-upload it as a PDF (text-layer) or as a text/Markdown file to enable preview.
          </div>
        )}

        {state.data && state.data.has_content && (
          <>
            {/* Sticky-ish search bar — sits at the top of the doc card so
                the matches counter + step buttons stay reachable on long
                docs. Browser CTRL+F still works; this is the discoverable
                affordance with prev/next + match counter built in. */}
            <div className="bylaw-search">
              <Icon name="search" size={16} />
              <input
                type="search"
                className="bylaw-search__input"
                aria-label="Search in this document"
                placeholder="Search in this document — Enter to jump to next match"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
              />
              {query && (
                <>
                  <span className="bylaw-search__count">
                    {matchCount === 0 ? "no matches"
                      : matchCount === 1 ? "1 match"
                      : `${activeIdx + 1} of ${matchCount}`}
                  </span>
                  <button
                    type="button"
                    className="bylaw-search__step"
                    onClick={() => step(-1)}
                    disabled={matchCount === 0}
                    title="Previous match (Shift+Enter)"
                    aria-label="Previous match"
                  >‹</button>
                  <button
                    type="button"
                    className="bylaw-search__step"
                    onClick={() => step(1)}
                    disabled={matchCount === 0}
                    title="Next match (Enter)"
                    aria-label="Next match"
                  >›</button>
                  <button
                    type="button"
                    className="bylaw-search__clear"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    title="Clear (Esc)"
                  >×</button>
                </>
              )}
            </div>

            <article
              ref={articleRef}
              className="card card--pad-lg"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 16,
                lineHeight: 1.7,
                color: "var(--ink-2)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {_renderHighlighted(state.data.content, query.trim(), activeIdx)}
            </article>
          </>
        )}
      </div>
    </div>
  );
}

// Escape a string so it's safe to embed inside a RegExp source. Used
// by the in-doc search so a user typing "C&R" or "$25" doesn't accidentally
// invoke regex metachars.
function _escapeReSrc(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Split `text` on case-insensitive occurrences of `query` and render
// each match in a <mark>. The match at `activeIdx` gets the --active
// class so it stands out for scroll-into-view. Min 2 chars to match —
// 1 char would highlight nearly everything.
function _renderHighlighted(text, query, activeIdx) {
  if (!text) return text;
  const q = (query || "").trim();
  if (q.length < 2) return text;
  const src = _escapeReSrc(q);
  const re = new RegExp(`(${src})`, "gi");
  const parts = text.split(re);
  let hitIdx = -1;
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      hitIdx += 1;
      const active = hitIdx === activeIdx;
      return (
        <mark
          key={i}
          className={`bylaw-hit ${active ? "bylaw-hit--active" : ""}`}
        >
          {part}
        </mark>
      );
    }
    return part;
  });
}

/* ─── Community directory ────────────────────────────────────────────────── */
const _DIRECTORY_ROLE_LABEL = {
  super_admin: "Operator",
  company_admin: "Company admin",
  manager: "Manager",
  homeowner: "Homeowner",
};
const _DIRECTORY_AVATAR_COLOR = {
  super_admin: "b",
  company_admin: "b",
  manager: "t",
  homeowner: "g",
};
function _directoryInitials(m) {
  const src = (m.full_name || "").trim();
  if (!src) return "··";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function CommunityDirectory({ onMessageNeighbor }) {
  const [state, setState] = useStateH({ loading: true, error: null, members: [] });
  const [query, setQuery] = useStateH("");

  useEffectH(() => {
    let cancelled = false;
    Api.communityDirectory()
      .then((members) => { if (!cancelled) setState({ loading: false, error: null, members: members || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || "Couldn't load directory.", members: [] }); });
    return () => { cancelled = true; };
  }, []);

  const { loading, error, members } = state;

  const q = query.trim().toLowerCase();
  const visibleMembers = q
    ? members.filter((m) => (m.full_name || "").toLowerCase().includes(q))
    : members;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 880 }}>
        <Banner icon="users">
          <b>Your neighbors.</b> Only names and roles are visible — never email
          addresses.
        </Banner>

        {!loading && !error && members.length > 0 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${members.length} ${members.length === 1 ? "neighbor" : "neighbors"} by name…`}
            aria-label="Search directory"
            className="input"
            style={{ width: "100%" }}
          />
        )}

        {loading && <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading directory…</div>}
        {error && <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{error}</div>}
        {!loading && !error && members.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            Nobody else has joined your community yet.
          </div>
        )}
        {!loading && !error && members.length > 0 && visibleMembers.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            No neighbors match "{query}".
          </div>
        )}
        {visibleMembers.length > 0 && (
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {visibleMembers.map((m) => {
              // Own card isn't clickable. Other cards open a message
              // compose pre-filled with this neighbor as recipient.
              // Backend gates the actual conversation behind the
              // accept/deny message-request flow.
              const clickable = !m.is_self && typeof onMessageNeighbor === "function";
              const handleOpen = () => {
                if (!clickable) return;
                onMessageNeighbor({
                  recipient_user_id: m.id,
                  recipient_name: m.full_name || "",
                });
              };
              const cardStyle = {
                padding: 16,
                cursor: clickable ? "pointer" : "default",
              };
              return (
                <div
                  className="card"
                  key={m.id}
                  style={cardStyle}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : -1}
                  onClick={handleOpen}
                  onKeyDown={(e) => {
                    if (clickable && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      handleOpen();
                    }
                  }}
                  aria-label={clickable ? `Message ${m.full_name || "neighbor"}` : undefined}
                >
                  <div className="row">
                    <span className={`avatar avatar--lg avatar--${_DIRECTORY_AVATAR_COLOR[m.role] || "g"}`}>
                      {_directoryInitials(m)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {m.full_name || "(no name)"}
                        {m.is_self && <span className="text-xs text-mute mono" style={{ marginLeft: 6 }}>· you</span>}
                      </div>
                      <div className="text-sm text-mute">{_DIRECTORY_ROLE_LABEL[m.role] || m.role}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export { HomeownerChat, HomeownerHistory, BylawsLibrary, CommunityDirectory };
